import React, { useCallback, useEffect, useState } from 'react';
import WindowBody from '../components/Window/WindowBody';
import WindowFooter from '../components/Window/WindowFooter';
import { UiButton } from '../components/UiButton';
import { get } from '../services/ApiService';
import UsinaResumoCards, { formatarNumero, formatarData } from '../components/Usina/UsinaResumoCards';
import UsinaGraficoDiario from '../components/Usina/UsinaGraficoDiario';
import UsinaGraficoMensal from '../components/Usina/UsinaGraficoMensal';
import UsinaCurvaPotencia from '../components/Usina/UsinaCurvaPotencia';
import '../components/Usina/Usina.scss';

const STATUS_LABEL: any = {
    online: 'Online',
    offline: 'Offline',
    alerta: 'Alerta',
};

/**
 * Drill-down em uma tela só: o gráfico do ano (embaixo) escolhe o mês,
 * o do mês (meio) escolhe o dia, e a curva (topo) mostra o dia escolhido.
 */
export default function UsinaDetalheViewer(props: any) {
    const usinaId = props.data?.id;
    const [detalhes, setDetalhes] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [loadingCurva, setLoadingCurva] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    const carregar = useCallback((ano?: number, mes?: number, data?: string) => {
        setLoading(true);

        const parametros = [
            ano ? `&ano=${ano}` : '',
            mes ? `&mes=${mes}` : '',
            data ? `&data=${data}` : '',
        ].join('');

        return get(`/?route=usina&action=detalhes&id=${usinaId}${parametros}`)
            .then((response: any) => {
                if (response.data?.success) {
                    setDetalhes(response.data);
                } else {
                    setErro(response.data?.message || 'Não foi possível carregar a usina');
                }
            })
            .catch((error: any) => setErro(error.message || 'Erro de comunicação com o servidor'))
            .finally(() => setLoading(false));
    }, [usinaId]);

    useEffect(() => {
        if (!usinaId) {
            setErro('Usina não informada');
            setLoading(false);
            return;
        }
        carregar();
    }, [usinaId, carregar]);

    const selecionarDia = (data: string) => {
        setLoadingCurva(true);
        get(`/?route=usina&action=curvaPotencia&id=${usinaId}&data=${data}`)
            .then((response: any) => {
                if (response.data?.success) {
                    setDetalhes((atual: any) => ({
                        ...atual,
                        curva: {
                            data: response.data.data,
                            registros: response.data.registros,
                            aviso: response.data.aviso,
                        },
                    }));
                }
            })
            .finally(() => setLoadingCurva(false));
    };

    const navegarMes = (delta: number) => {
        let { ano, mes } = detalhes.referencia;
        mes += delta;
        if (mes < 1) { mes = 12; ano -= 1; }
        if (mes > 12) { mes = 1; ano += 1; }
        carregar(ano, mes);
    };

    if (loading && !detalhes) {
        return (
            <>
                <WindowBody><div className="usina-vazio">Carregando dados da usina...</div></WindowBody>
                <WindowFooter>
                    <UiButton variant="secondary" onClick={props.triggerClose}>Fechar</UiButton>
                </WindowFooter>
            </>
        );
    }

    if (erro || !detalhes) {
        return (
            <>
                <WindowBody><div className="usina-vazio">{erro || 'Usina não encontrada'}</div></WindowBody>
                <WindowFooter>
                    <UiButton variant="secondary" onClick={props.triggerClose}>Fechar</UiButton>
                </WindowFooter>
            </>
        );
    }

    const { usina, metricas, acumulado, referencia, anos_disponiveis, ultima_data, serie_mensal, serie_diaria, curva } = detalhes;
    const diaResumo = serie_diaria.find((item: any) => item.data === curva?.data) || null;

    return (
        <>
            <WindowBody>
                <div className="usina-detalhe">
                    <div className="usina-header">
                        <div>
                            <h4>{usina.nome}</h4>
                            <div className="usina-subtitulo">
                                {formatarNumero(usina.capacidade)} kWp
                                {` · ${STATUS_LABEL[usina.status] || usina.status || 'sem status'}`}
                                {usina.endereco ? ` · ${usina.endereco}` : ''}
                            </div>
                        </div>
                        <div className="usina-subtitulo">
                            Último dado: {formatarData(ultima_data)}
                        </div>
                    </div>

                    <UsinaResumoCards metricas={metricas} acumulado={acumulado} referencia={referencia} />

                    <div className="usina-linha">
                        <UsinaGraficoMensal
                            serie={serie_mensal}
                            ano={referencia.ano}
                            anos={anos_disponiveis}
                            mesSelecionado={referencia.mes}
                            onSelecionarMes={(mes: number) => carregar(referencia.ano, mes)}
                            onTrocarAno={(ano: number) => carregar(ano, referencia.mes)}
                            loading={loading}
                        />

                        <UsinaGraficoDiario
                            serie={serie_diaria}
                            referencia={referencia}
                            diaSelecionado={curva?.data}
                            estimadoMensal={metricas.estimado_mensal}
                            onSelecionarDia={selecionarDia}
                            onNavegarMes={navegarMes}
                            loading={loading}
                        />
                    </div>

                    <UsinaCurvaPotencia
                        curva={curva}
                        diaResumo={diaResumo}
                        loading={loadingCurva}
                    />

                </div>
            </WindowBody>

            <WindowFooter>
                <UiButton variant="secondary" onClick={props.triggerClose}>Fechar</UiButton>
            </WindowFooter>
        </>
    );
}
